/**
 * Der Zeitraum, den sich die Dashboard-Widgets teilen.
 *
 * Gewählt wird er im Umschalter des großen Nutzungs-Widgets; der „Heute"-Ring
 * daneben soll denselben Zeitraum zeigen. Beide sind aber eigenständig
 * registrierte `dashboardWidgets` — `DashboardGrid` rendert sie als
 * `<widget.component />` **ohne Props**, und das Grid liegt in core-frontend.
 * Es gibt also keinen gemeinsamen Elternteil in dieser App, durch den ein
 * Context laufen könnte.
 *
 * Deshalb ein Modul-Store statt eines Providers: beide Widgets importieren
 * denselben Haken, ohne dass irgendwo eine Klammer um sie herum nötig wäre.
 * `useSyncExternalStore` ist hier die richtige Form und kein Overkill — es hält
 * die Widgets auch dann synchron, wenn React sie in getrennten Durchläufen
 * rendert.
 *
 * ⚪ Der Wert überlebt bewusst das Aus- und Einhängen des Dashboards: Wer 90
 * Tage gewählt hat und zurück auf `/dashboard` kommt, will nicht wieder bei 30
 * anfangen. Er ist eine reine Anzeige-Einstellung — keine Nutzerdaten —, ein
 * Modul-Zustand ist dafür unbedenklich.
 */

import type { HistoryGranularity } from '@xhubio-saas/core-frontend/plugin-usage'
import { useSyncExternalStore } from 'react'
import type { RangeDays } from './dashboardUi'

/** `'today'` oder eine der Voreinstellungen aus {@link RangeDays}. */
export type DashboardRange = 'today' | RangeDays

/** Startwert: 30 Tage — genug Verlauf, um ein Muster zu sehen. */
let current: DashboardRange = 30

const listeners = new Set<() => void>()

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

function getSnapshot(): DashboardRange {
  return current
}

/** Setzt den Zeitraum für alle Dashboard-Widgets. */
export function setDashboardRange(next: DashboardRange): void {
  if (next === current) return
  current = next
  for (const listener of listeners) listener()
}

/** Der aktuell gewählte Zeitraum, re-rendert bei jeder Änderung. */
export function useDashboardRange(): DashboardRange {
  // Dritter Parameter (getServerSnapshot) = derselbe Wert: Der Store ist reiner
  // Modul-Zustand, es gibt keinen abweichenden Server-Stand.
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export interface RangeWindow {
  startDate: Date
  endDate: Date
  granularity: HistoryGranularity
}

/**
 * Rechnet den gewählten Zeitraum in ein Abfrage-Fenster um.
 *
 * 🔴 Beide Widgets müssen durch **diese** Funktion gehen. Zwei getrennte
 * Berechnungen wären zwei Abfragen mit minimal verschiedenen Grenzen — Ring und
 * Diagramm zeigten dann Zahlen, die sich um einzelne Aufrufe unterscheiden, und
 * niemand fände den Grund.
 */
export function rangeToWindow(range: DashboardRange): RangeWindow {
  if (range === 'today') {
    // UTC-Tagesgrenzen: das Backend bucketet nach UTC-Tagen.
    const startDate = new Date()
    startDate.setUTCHours(0, 0, 0, 0)
    const endDate = new Date(startDate)
    endDate.setUTCHours(23, 59, 59, 999)
    return { startDate, endDate, granularity: 'hour' }
  }
  const endDate = new Date()
  const startDate = new Date(endDate)
  startDate.setDate(startDate.getDate() - range)
  return { startDate, endDate, granularity: 'day' }
}

/**
 * i18n-Schlüssel für die Beschriftung eines Zeitraums — dieselben Texte, die
 * der Umschalter trägt, damit die Überschrift des Rings nicht neu übersetzt
 * werden muss.
 */
export function rangeLabelKey(range: DashboardRange): { key: string; fallback: string } {
  return range === 'today'
    ? { key: 'dashboard.range.today', fallback: 'Heute' }
    : { key: `dashboard.range.days${range}`, fallback: `${range} Tage` }
}
